import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';

/**
 * Reusable dropdown control for heatmap settings
 */
const HeatmapDropdownControl = ({
  id,
  label,
  options,
  selectedValue,
  isMenuOpen,
  onToggleMenu,
  onSelectOption,
  menuClassName
}) => {
  const selectedName = options.find((opt) => opt.value === selectedValue)?.name || selectedValue;

  return (
    <div className="control-row">
      <IconButton
        id={id}
        innerText={
          <>
            {label}: <font className="bold">{selectedName}</font>
          </>
        }
        icon={isMenuOpen ? ICON_BUTTON_ICONS.CHEVRON_UP : ICON_BUTTON_ICONS.CHEVRON_DOWN}
        iconOnRight
        type={ICON_BUTTON_TYPES.BLUE}
        onClick={onToggleMenu}
        filled={isMenuOpen}
      />
      {isMenuOpen && (
        <div className={`heatmap-control-menu-wrap ${menuClassName}`}>
          {options.map((option) => (
            <IconButton
              key={option.value}
              hoverType={ICON_BUTTON_TYPES.BLUE}
              withBorder={false}
              innerText={option.name}
              onClick={() => onSelectOption(option.value) & onToggleMenu()}
              disabled={selectedValue === option.value}
              filled={selectedValue === option.value}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * HeatmapControls - Shared heatmap control component
 *
 * Controls for heatmap visualization:
 * - Tile size selector (revenue, employees, etc.)
 * - Color coding selector (sector, country, etc.)
 * - Grouping toggle
 *
 * Used by both ComparableCompaniesHeader (desktop) and ComparableCompaniesHeaderMobile
 */
const HeatmapControls = observer(function HeatmapControls({ state }) {
  return (
    <div className="profiler-heatmap-controls-header">
      <HeatmapDropdownControl
        id="profiler-tile-size-metric"
        label="Tile size"
        options={state.profilerHeatmapTileSizeOptions}
        selectedValue={state.profilerHeatmapTileSize}
        isMenuOpen={state.isProfilerHeatmapTileSizeMenuToggled}
        onToggleMenu={state.toggleProfilerHeatmapTileSizeMenu}
        onSelectOption={state.setProfilerHeatmapTileSize}
        menuClassName="tile-size"
      />

      <HeatmapDropdownControl
        id="profiler-color-coding-metric"
        label="Color coding"
        options={state.profilerHeatmapColorCodingOptions}
        selectedValue={state.profilerHeatmapColorCoding}
        isMenuOpen={state.isProfilerHeatmapColorCodingMenuToggled}
        onToggleMenu={state.toggleProfilerHeatmapColorCodingMenu}
        onSelectOption={state.setProfilerHeatmapColorCoding}
        menuClassName="color-coding"
      />

      {/* Grouping Toggle */}
      {!state.disableProfilerHeatmapGroup && (
        <div className="control-row">
          <IconButton
            id="profiler-grouping-toggle"
            type={ICON_BUTTON_TYPES.BLUE}
            icon={
              state.isProfilerHeatmapGroupingToggled
                ? ICON_BUTTON_ICONS.TOGGLE_ON
                : ICON_BUTTON_ICONS.TOGGLE_OFF
            }
            innerText="Grouping"
            onClick={state.toggleProfilerHeatmapGrouping}
            filled={state.isProfilerHeatmapGroupingToggled}
            disabled={state.disableProfilerHeatmapGroup}
          />
        </div>
      )}
    </div>
  );
});

HeatmapDropdownControl.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedValue: PropTypes.string,
  isMenuOpen: PropTypes.bool,
  onToggleMenu: PropTypes.func.isRequired,
  onSelectOption: PropTypes.func.isRequired,
  menuClassName: PropTypes.string
};

HeatmapControls.propTypes = {
  state: PropTypes.shape({
    profilerHeatmapTileSizeOptions: PropTypes.array.isRequired,
    profilerHeatmapTileSize: PropTypes.string,
    isProfilerHeatmapTileSizeMenuToggled: PropTypes.bool,
    toggleProfilerHeatmapTileSizeMenu: PropTypes.func.isRequired,
    setProfilerHeatmapTileSize: PropTypes.func.isRequired,
    profilerHeatmapColorCodingOptions: PropTypes.array.isRequired,
    profilerHeatmapColorCoding: PropTypes.string,
    isProfilerHeatmapColorCodingMenuToggled: PropTypes.bool,
    toggleProfilerHeatmapColorCodingMenu: PropTypes.func.isRequired,
    setProfilerHeatmapColorCoding: PropTypes.func.isRequired,
    disableProfilerHeatmapGroup: PropTypes.bool,
    isProfilerHeatmapGroupingToggled: PropTypes.bool,
    toggleProfilerHeatmapGrouping: PropTypes.func
  }).isRequired
};

export default HeatmapControls;
